import ReadingCoachSelect from './reading_coach_select'

class ReadingCoachDemo {
  constructor (element) {
    this.element = element
    this.generateButton = this.element.querySelector('.js-generate-button')
    this.randomizeButton = this.element.querySelector('.js-randomize-button')
    this.contentArea = this.element.querySelector('.js-content')
    this.book = this.element.querySelector('.js-book')
    this.transitionContainer = this.element.querySelector('.js-demo-story')
    this.shareSection = this.element.querySelector('.js-share')
    this.shareWidgets = [...this.element.querySelectorAll('.js-share [data-key]')]
    this.parentThemeElement = document.body
    this.themeSwitcher = document.querySelector('.js-reading-coach-theme')
    this.themeSwitcherReset = document.querySelector('.js-reading-coach-theme-reset')
    this.loadingOverlay = document.querySelector('.js-loading-overlay')
    this.loadingInner = this.loadingOverlay.querySelector('.js-loading-inner')

    this.themeSwitcherReset.dataset.theme = this.parentThemeElement.dataset.theme

    const selects = [...this.element.querySelectorAll('.js-reading-coach-select')]

    this.selects = selects.map(element => {
      return new ReadingCoachSelect(element)
    })

    this.registerSelectionUpdateEvents()

    this.generateButton.addEventListener('click', () => {
      this.updateStory()
    })

    this.randomizeButton?.addEventListener('click', () => {
      this.selects.forEach(select => {
        select.setRandom()
      })

      this.updateStory()
    })

    this.selects.forEach(select => {
      if ('random' in select.element.dataset) {
        select.setRandom()
      }
    })

    this.updateButtonState()

    const { key } = this.getSelectionData()
    this.updateShareWidget(key)
  }

  updateStory = () => {
    const {
      key,
      level,
      data: storyData
    } = this.getSelectionData()

    this.transitionContainer.setAttribute('aria-busy', true)
    this.element.classList.remove('closed')
    this.updateLoadingIcons()
    if (!document.documentElement.classList.contains('reduced-motion')) {
      // If motion is enabled, set a transitionend listener to update and the
      // content after hidden and then show it again
      this.loadingOverlay.addEventListener('transitionend', () => {
        if (this.transitionContainer.classList.contains('loading')) {
          this.hideLoadingOverlay(storyData, key, level)
        }
      })
      this.transitionContainer.classList.add('loading')
    } else {
      // If motion is disabled, hide the content and use setTimeout to swap
      // and show it again.
      this.transitionContainer.classList.add('loading')
      setTimeout(() => {
        this.hideLoadingOverlay(storyData, key, level)
      }, 500)
    }
  }

  hideLoadingOverlay = (storyData, key, level) => {
    this.updateStoryContent(storyData.story, level)
    this.updateBookCover(storyData.book_cover, storyData.alt)
    this.updateShareWidget(key)
    setTimeout(() => {
      this.setTheme(storyData.theme)
      this.transitionContainer.classList.remove('loading')
      this.transitionContainer.setAttribute('aria-busy', false)
    }, 750)
  }

  registerSelectionUpdateEvents = () => {
    this.selects[0].onAfterUpdate = (value) => {
      this.updateButtonState()
    }

    this.selects[1].onAfterUpdate = (value) => {
      const indefiniteArticle = this.element.querySelector('.js-ia--setting')
      if (value === 'space') {
        indefiniteArticle.innerHTML = ''
      } else {
        indefiniteArticle.innerHTML = 'a'
      }

      this.updateButtonState()
    }

    this.selects[2].onAfterUpdate = (value) => {
      const indefiniteArticle = this.element.querySelector('.js-ia--level')
      if (value === 'advanced') {
        indefiniteArticle.innerHTML = 'an'
      } else {
        indefiniteArticle.innerHTML = 'a'
      }

      this.updateButtonState()
    }
  }

  updateLoadingIcons = () => {
    this.loadingInner.replaceChildren()
    document.querySelectorAll('.selected-option .js-icon').forEach(icon => {
      const loader = document.createElement('div')
      loader.style = icon.getAttribute('style')
      loader.classList.add('loader')
      this.loadingInner.appendChild(loader)
    })
  }

  getSelectionData = () => {
    const results = {}

    this.selects.forEach(({ element }) => {
      results[element.dataset.key] = element.dataset.value
    })

    const key = `${results.character}_${results.setting}_${results.level}`

    return {
      level: results.level,
      key: key,
      data: window.rcdData[key]
    }
  }

  updateButtonState = () => {
    const optionsSet = this.selects.every(({ element }) => {
      return element.dataset.value !== undefined
    })

    this.generateButton.disabled = !optionsSet
  }

  updateStoryContent = (content, level) => {
    this.contentArea.innerHTML = content
    this.contentArea.dataset.size = level
    this.contentArea.scrollTop = 0
  }

  // The book cover is updated by swapping out the value of a CSS variable used
  // for the background image.
  updateBookCover = (bookCover, alt) => {
    this.book.style.setProperty('--book-cover', bookCover ? `url(${bookCover}` : '')
    this.book.ariaLabel = bookCover ? alt : ''
  }

  // Every share widget is created on page load and visually hidden until
  // needed. It's done this way because they don't initialize properly if added
  // dynamically. They are also set to visible on page load and then hidden for
  // similar reasons.
  updateShareWidget = (key) => {
    this.shareSection.classList.remove('hidden')
    this.shareWidgets.forEach(widget => {
      if (widget.dataset.key === key) {
        widget.classList.remove('hidden')
        widget.ariaHidden = false
      } else {
        widget.classList.add('hidden')
        widget.ariaHidden = true
      }
    })
  }

  setTheme = (theme) => {
    const newTheme = theme || this.themeSwitcherReset.dataset.theme

    this.themeSwitcher.dataset.theme = newTheme

    // Only immediately set the theme if the user is currently scrolled into the
    // demo components theme "zone" (between the theme switcher and reset)
    const halfWindowHeight = window.innerHeight / 2
    if (this.themeSwitcher.getBoundingClientRect().top < halfWindowHeight) {
      this.parentThemeElement.dataset.theme = newTheme
    }
  }
}

export default {
  selector: '.js-reading-coach-demo',
  Source: ReadingCoachDemo
}
